import React from "react";
import PropTypes from "prop-types";
import { isNumber } from "lodash";
import { getPathogenicityColorByAllNames } from "../../../../../common/helpers/Constant";
import { areEqual } from "../../../../../common/helpers/helpers";
import HelperText from "../../../../../components/atoms/HelperText";

const { shape, string, number, bool } = PropTypes;

const pointsExplanation = (
  <div>
    <p>
      Rules are combined using the point system described in{" "}
      <a
        href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC8011844/"
        target="_blank"
      >
        PMID:32720330
      </a>
    </p>
    <p>
      Each rule triggered is assigned a number of points based on the strength
      of the evidence provided:
    </p>
    <div className="tw-my-2">
      <ul>
        <li>
          <b>Supporting</b>: 1 point
        </li>
        <li>
          <b>Moderate:</b> 2 points
        </li>
        <li>
          <b>Strong:</b> 4 points
        </li>
        <li>
          <b>Very Strong:</b> 8 points
        </li>
      </ul>
    </div>
    <p>
      A total score is computed as the sum of the points from the pathogenic
      rules, minus the sum of the points from benign rules.
    </p>
    <p>
      The total score is then compared to thresholds to assign the final
      verdict:
    </p>
    <div className="tw-my-2">
      <ul>
        <li>
          <b>Pathogenic</b> if greater than or equal to 10.
        </li>
        <li>
          <b>Likely Pathogenic</b> if between 6 and 9 inclusive.
        </li>
        <li>
          <b>Uncertain Significance</b> if between 0 and 5.
        </li>
        <li>
          <b>Likely Benign</b> if between -6 and -1.
        </li>
        <li>
          <b>Benign</b> if less than or equal to -7.
        </li>
      </ul>
    </div>
  </div>
);

const ACMGScores = ({ verdictRules, showExplanations, customFontSize }) => {
  if (
    !verdictRules ||
    !Object.keys(verdictRules).length ||
    (!verdictRules?.total_score && !isNumber(verdictRules?.total_score))
  )
    return null;

  const {
    total_score: totalScore,
    benign_score: benignScore,
    pathogenic_score: pathogenicScore,
    benign_subscore: benignSubscore,
    pathogenic_subscore: pathogenicSubscore,
    verdict,
  } = verdictRules;

  return (
    <div
      className={`${customFontSize || "tw-text-base"} tw-font-sans tw-flex`}
      id="ACMG_scores_varsome"
      data-testid="ACMG_scores"
    >
      <div
        data-testid="ACMG_scores_total_score"
        className="tw-font-bold"
        style={{
          color: getPathogenicityColorByAllNames(verdict),
        }}
      >
        {totalScore}
      </div>
      <div className="tw-ml-0.5">points =</div>
      <div
        className="tw-font-bold tw-ml-1"
        style={{ color: getPathogenicityColorByAllNames(pathogenicSubscore) }}
      >
        {pathogenicScore || 0}
      </div>
      <div className="tw-ml-0.5">P</div>
      <div className="tw-ml-0.5">-</div>
      <div
        className="tw-font-bold tw-ml-1"
        style={{ color: getPathogenicityColorByAllNames(benignSubscore) }}
      >
        {benignScore || 0}
      </div>
      <div className="tw-ml-0.5">B</div>
      <div className="tw-text-sm">
        {showExplanations && (
          <HelperText
            RenderContent={() => pointsExplanation}
            title="Germline Variant Classification Scores"
          />
        )}
      </div>
    </div>
  );
};

ACMGScores.propTypes = {
  verdictRules: shape({
    approx_score: number,
    benign_subscore: string,
    clinical_score: number,
    pathogenic_subscore: string,
    verdict: string,
    benign_score: number,
    pathogenic_score: number,
    total_score: number,
  }),
  showExplanations: bool,
  customFontSize: string,
};

ACMGScores.defaultProps = {
  verdictRules: null,
  showExplanations: true,
  customFontSize: "",
};
export default React.memo(ACMGScores, areEqual);
